import FilterChip from 'LuxKit/Chips/FilterChip'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import styled from 'styled-components'
import { rem } from 'polished'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import RadioInput from 'LuxKit/Radio/RadioInput'
import Group from 'components/utils/Group'
import useCustomerRatingOptions from 'hooks/Search/useCustomerRatingOptions'
import useToggle from 'hooks/useToggle'
import {
  deleteSearchParams,
  setSearchParamValue,
} from 'lib/url/searchUrlUtils'
import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Modal from 'LuxKit/Modal/Modal'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import type { FilterControlChangeHandler } from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'

const RadioGroup = styled(Group)`
  padding-inline: ${rem(16)};
`

interface Props {
  filters: App.OfferListFilters
  search: string
  label: string
  place?: App.Place
  onFilterChange: FilterControlChangeHandler
  largeFilterChipSize?: boolean
}

function CustomerRatingDropdown(props: Props) {
  const { filters, search, label, onFilterChange, largeFilterChipSize = false } = props
  const intl = useIntl()
  const { CUSTOMER_RATING_GTE } = useCustomerRatingOptions()

  const customerRatingOptions = useMemo(() => [
    CUSTOMER_RATING_GTE.EXCEPTIONAL,
    CUSTOMER_RATING_GTE.VERY_GOOD,
    CUSTOMER_RATING_GTE.GOOD,
    CUSTOMER_RATING_GTE.ANY_RATING,
  ], [CUSTOMER_RATING_GTE])

  const applyText = intl.formatMessage({
    id: 'ttBmnxKIk+',
    defaultMessage: 'Apply',
    description: 'Button text to apply customer rating filter in the filters panel',
  })

  const isMobile = useScreenSizeOnly('mobile')
  const [isDropdownOpen, toggleDropDown, , closeDropdown] = useToggle()
  const filterChipRef = React.useRef<HTMLButtonElement>(null)
  const selectedRating = filters?.customerRatingGte ?? CUSTOMER_RATING_GTE.ANY_RATING.value

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value)
    let newSearchParams: string
    if (value === CUSTOMER_RATING_GTE.ANY_RATING.value) {
      newSearchParams = deleteSearchParams(search, 'customerRatingGte')
    } else {
      newSearchParams = setSearchParamValue(search, 'customerRatingGte', e.target.value)
    }

    onFilterChange({ search: newSearchParams })
    closeDropdown()
  }, [closeDropdown, onFilterChange, search, CUSTOMER_RATING_GTE])

  const hasRatingBeenSelected = selectedRating !== CUSTOMER_RATING_GTE.ANY_RATING.value && !!selectedRating
  const chipLabel = label + (hasRatingBeenSelected ? ` (${selectedRating}.0+)` : '')

  return (
    <div>
      <FilterChip
        selected={hasRatingBeenSelected}
        ref={filterChipRef}
        size={largeFilterChipSize ? 'large' : 'medium'}
        onClick={toggleDropDown}
        endIcon={isDropdownOpen ? <LineAngleUpIcon /> : <LineAngleDownIcon />}
      >
        {chipLabel}
      </FilterChip>
      <CSSBreakpoint min="tablet">
        <DropdownList
          size="S"
          triggerRef={filterChipRef}
          anchorRef={filterChipRef}
          open={isDropdownOpen}
          placement="bottom-start"
          onClose={closeDropdown}
        >
          <RadioGroup direction="vertical" gap={16}>
            {customerRatingOptions.map(option => (
              <RadioInput
                key={option.label}
                checked={option.value == selectedRating}
                onChange={onChange}
                name={option.label}
                value={option.value}
              >
                {option.label}
              </RadioInput>
            ))}
          </RadioGroup>
        </DropdownList>
      </CSSBreakpoint>
      <CSSBreakpoint max="mobile">
        <Modal
          title={label}
          isOpen={isDropdownOpen && isMobile}
          onClose={closeDropdown}
          primaryActionText={applyText}
          onPrimaryActionClick={closeDropdown}
        >
          <Group direction="vertical" gap={16}>
            {customerRatingOptions.map(option => (
              <RadioInput
                key={option.label}
                checked={option.value == selectedRating}
                onChange={onChange}
                name={option.label}
                value={option.value}
              >
                {option.label}
              </RadioInput>
            ))}
          </Group>
        </Modal>
      </CSSBreakpoint>
    </div>
  )
}

export default CustomerRatingDropdown
